// ══════════════════════════════════════════════════════════════════
// CartHub Dashboard — Carts Component
// Lista carrelli abbandonati con filtri, badge stato, dettaglio.
// Include CartStatusBadge (badge recovery_type) e CartDetail.
// Testi UI: namespace `carts` (src/locales/*/carts.json).
// ══════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api, ago, CopyBtn, Ic, LegendInfoIcon, useMediaMinWidth } from "../shared";
import { formatMoney } from "../i18n";

/** WooCommerce admin — edit order (classic `post.php`; compatible with most shops). */
function wcOrderEditUrl(domain, orderId) {
  if (!domain || orderId == null || String(orderId).trim() === "") return "";
  const host = domain.replace(/^https?:\/\//i, "").split("/")[0].trim();
  if (!host) return "";
  const id = encodeURIComponent(String(orderId).trim());
  return `https://${host}/wp-admin/post.php?post=${id}&action=edit`;
}

/** Dates formatted in Europe/Rome (aligned with backend cron semantics). */
function formatRecoveryHintDate(iso, lang) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(lang || "it", {
    timeZone: "Europe/Rome",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Recovery queue popup/detail copy from API `code` + `meta`. */
function recoveryHintText(hint, t, lang) {
  if (!hint) return "";
  if (hint.message && typeof hint.message === "string" && hint.code == null) return hint.message;
  const m = hint.meta || {};
  const fd = (iso) => formatRecoveryHintDate(iso, lang);
  switch (hint.code) {
    case "sequence_r3_sent":
      return t("recoveryHints.sequence_r3_sent");
    case "awaiting_r3":
      return t("recoveryHints.awaiting_r3");
    case "awaiting_r2":
      return t("recoveryHints.awaiting_r2");
    case "shop_inactive":
      return t("recoveryHints.shop_inactive");
    case "shadow_mode":
      return t("recoveryHints.shadow_mode");
    case "email_paused":
      return t("recoveryHints.email_paused");
    case "deliverability_suspended":
      return t("recoveryHints.deliverability_suspended");
    case "silent_period":
      return m.silentUntil ? t("recoveryHints.silent_period.withDate", { date: fd(m.silentUntil) }) : t("recoveryHints.silent_period.noDate");
    case "no_email":
      return t("recoveryHints.no_email");
    case "email_suppressed":
      return t("recoveryHints.email_suppressed");
    case "r1_not_scheduled":
      return t("recoveryHints.r1_not_scheduled");
    case "abandon_too_recent":
      if (m.variant === "with_queue" && m.abandonedAt && m.queueFrom) {
        return t("recoveryHints.abandon_too_recent.withQueue", { abandoned: fd(m.abandonedAt), queueFrom: fd(m.queueFrom) });
      }
      if (m.variant === "abandon_only" && m.abandonedAt) {
        return t("recoveryHints.abandon_too_recent.abandonOnly", { abandoned: fd(m.abandonedAt) });
      }
      return t("recoveryHints.abandon_too_recent.minimal");
    case "frequency_cooldown_72h":
      return (
        t("recoveryHints.frequency_cooldown_72h.body") +
        (m.scheduledFuture ? t("recoveryHints.frequency_cooldown_72h.extra", { future: fd(m.scheduledFuture) }) : "")
      );
    case "r1_scheduled_future":
      return m.scheduled ? t("recoveryHints.r1_scheduled_future.withDate", { date: fd(m.scheduled) }) : t("recoveryHints.r1_scheduled_future.noDate");
    case "deliverability_throttled":
      return m.scheduled ? t("recoveryHints.deliverability_throttled.withDate", { date: fd(m.scheduled) }) : t("recoveryHints.deliverability_throttled.noDate");
    case "r1_queued":
      return m.scheduled ? t("recoveryHints.r1_queued.withDate", { date: fd(m.scheduled) }) : t("recoveryHints.r1_queued.noDate");
    case "dedup_wc_order_loser": {
      const lid = m.kept_cart_id || m.keptCartId || m.leader_cart_id || m.leaderCartId;
      if (lid) {
        const short = String(lid).replace(/-/g, "").slice(0, 8);
        return t("recoveryHints.dedup_wc_order_loser_with_leader", { leaderId: short });
      }
      return t("recoveryHints.dedup_wc_order_loser");
    }
    default:
      return hint.message || "";
  }
}

/** Segnaposto quando il cliente non ha nome salvato (stesso slot della riga elenco). */
function CartNamePlaceholder() {
  return (
    <span style={{ display: "inline-flex", alignItems: "center" }} aria-hidden>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="21" r="1" />
        <circle cx="20" cy="21" r="1" />
        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
      </svg>
    </span>
  );
}

/** Icona minimale (orario + info): apre il popup sulla coda R1–R3 */
function RecoveryQueueHintTrigger({ onOpen }) {
  const { t } = useTranslation("carts");
  const label = t("legend.recoveryQueueHint");
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      aria-label={label}
      title={label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        marginLeft: 4,
        padding: 5,
        border: "none",
        background: "transparent",
        cursor: "pointer",
        borderRadius: 8,
        color: "#0A85CF",
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#f0faf8";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.65" />
        <path d="M12 8v4l2.5 1.5" stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

/** Modale testo: coda R1–R3 o carrelli senza email */
function StatusHintModal({ title, message, onClose }) {
  const { t } = useTranslation("carts");
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!message) return null;
  const dialogTitle = title || t("legend.modalInfo");
  const titleId = "status-hint-dialog-title";
  return (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        background: "rgba(15, 23, 42, 0.45)",
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 12,
          maxWidth: 480,
          width: "100%",
          boxShadow: "var(--shadow-module)",
          padding: "22px 24px",
        }}
      >
        <h3 id={titleId} style={{ margin: 0, fontSize: 17, fontWeight: 600, color: "#111827" }}>
          {dialogTitle}
        </h3>
        <p style={{ margin: "14px 0 0", fontSize: 14, lineHeight: 1.65, color: "#374151", whiteSpace: "pre-line" }}>{message}</p>
        <button type="button" className="ch-btn ch-btn-primary ch-btn-sm" style={{ marginTop: 20 }} onClick={onClose}>
          {t("legend.modalClose")}
        </button>
      </div>
    </div>
  );
}

function ProvisionalHintTrigger({ onOpen }) {
  const { t } = useTranslation("carts");
  const label = t("legend.provisionalHint");
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      aria-label={label}
      title={label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        marginLeft: 4,
        padding: 5,
        border: "none",
        background: "transparent",
        cursor: "pointer",
        borderRadius: 8,
        color: "#64748b",
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#f1f5f9";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.65" />
        <path d="M12 8v4l2.5 1.5" stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

/** Tronca stringhe lunghe (referrer / path) per tabella dettaglio. */
function truncateDisplay(str, max) {
  const x = String(str ?? "").trim();
  if (!x) return "—";
  return x.length <= max ? x : `${x.slice(0, Math.max(1, max - 1))}…`;
}

/** Torta copertura: nota (blu → azzurro) vs non nota (bianco → grigio). */
function funnelCoveragePieBackground(pctTracked, pctUntracked) {
  const a = Math.max(0, Math.min(100, Number(pctTracked) || 0));
  const b = Math.max(0, Math.min(100, Number(pctUntracked) || 0));
  const BLUE_A = "rgb(10, 133, 207)";
  const BLUE_B = "rgb(41, 175, 255)";
  const UNT_A = "rgb(255, 255, 255)";
  const UNT_B = "rgb(211, 211, 211)";
  const EMPTY = "rgb(229, 231, 235)";
  if (a === 0 && b === 0) return `conic-gradient(${EMPTY} 0deg 360deg)`;
  if (a === 0) return `conic-gradient(${UNT_A} 0deg, ${UNT_B} 360deg)`;
  if (b === 0) return `conic-gradient(${BLUE_A} 0deg, ${BLUE_B} 360deg)`;
  const degA = (a / (a + b)) * 360;
  return `conic-gradient(${BLUE_A} 0deg, ${BLUE_B} ${degA}deg, ${UNT_A} ${degA}deg, ${UNT_B} 360deg)`;
}

/** Due fasi (solo ciò che il plugin registra con `page_exit` su carrello/checkout — vedi `funnelContext.panelIntroScope`). */
function FunnelStagePctRows({ wEp, denom, sc, sch, pc, pch }) {
  const { t } = useTranslation("carts");
  const row = (titleKey, descKey, p, count, pctTone) => (
    <div className="ch-funnel-stage-split__row ch-funnel-stage-split__row--pct-first">
      <span className={`ch-funnel-stage-split__pct-start ch-funnel-stage-split__pct-start--${pctTone}`}>{p}%</span>
      <p className="ch-funnel-stage-split__line-text">
        <strong>{t(titleKey)}</strong>
        {" — "}
        {t(descKey)}
        {wEp > 0 && count != null && !Number.isNaN(Number(count)) ? (
          <span className="ch-funnel-stage-split__line-count">
            {" "}
            ({t("funnelContext.stageCountOnly", { count: Number(count) })})
          </span>
        ) : null}
      </p>
    </div>
  );

  return (
    <div className="ch-funnel-col ch-funnel-col--stages-split">
      <p className="ch-funnel-col__title">
        {wEp > 0 ? t("funnelContext.stageStackTitle", { n: wEp }) : t("funnelContext.stageSplitTitleNoSample")}
      </p>
      {wEp > 0 && denom === 0 ? (
        <p className="ch-funnel-stage-split__line-text" style={{ fontSize: 13, color: "#6b7280", marginBottom: 8 }}>
          {t("funnelContext.stageNoCartCheckoutInSample")}
        </p>
      ) : null}
      <div
        className="ch-funnel-stage-split ch-funnel-stage-split--legend-only"
        aria-label={t("funnelContext.stageStackAria", {
          n: Math.max(0, wEp),
          cart: sc,
          checkout: sch,
        })}
      >
        {row("funnelContext.stageCartTitle", "funnelContext.stageCartDesc", pc, sc, "cart")}
        {row("funnelContext.stageCheckoutTitle", "funnelContext.stageCheckoutDesc", pch, sch, "checkout")}
      </div>
    </div>
  );
}

/** Variante insight testuale (solo riparto carrello vs checkout; vedi panelIntroScope). */
function funnelInsightVariant(fc) {
  const total = Number(fc?.total) || 0;
  const wEp = Number(fc?.with_exit_page) || 0;
  const sc = Number(fc?.stage_cart) || 0;
  const sch = Number(fc?.stage_checkout) || 0;
  const denom = sc + sch;
  if (total === 0) return "empty";
  if (wEp === 0) return "noTracked";
  if (denom === 0) return "noCartCheckoutClassified";
  if (sc === denom && sch === 0) return "allCart";
  if (sch === denom && sc === 0) return "allCheckout";
  return "mixed";
}

/** Cruscotto funnel — 3 colonne: copertura (torta 2 segmenti + testi) | fasi (% in testa a ogni riga) | sintesi. */
function FunnelContextCoverageCard({ fc }) {
  const { t } = useTranslation("carts");
  const isDesktop = useMediaMinWidth(900);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!isDesktop) setMobileOpen(false);
  }, [isDesktop]);

  if (fc == null) return null;

  const days = Number(fc.window_days) || 30;
  const total = Number(fc.total) || 0;
  const wEp = Number(fc.with_exit_page) || 0;
  const pctMain =
    fc.pct_exit_page == null || Number.isNaN(Number(fc.pct_exit_page))
      ? null
      : Math.min(100, Math.round(Number(fc.pct_exit_page)));

  const sc = Number(fc.stage_cart) || 0;
  const sch = Number(fc.stage_checkout) || 0;
  const denom = sc + sch;
  let pc = 0;
  let pch = 0;
  if (wEp > 0 && denom > 0) {
    pc = Math.min(100, Math.round((100 * sc) / denom));
    pch = Math.max(0, 100 - pc);
  }
  const pctUntracked =
    total > 0 ? Math.min(100, Math.round(((total - wEp) / total) * 100)) : 0;

  const insightVar = funnelInsightVariant(fc);

  const panelId = "carts-funnel-legend-panel";
  const showPanel = isDesktop || mobileOpen;

  const insightBody =
    wEp > 0
      ? t(`funnelContext.insight.${insightVar}`)
      : total > 0
        ? t("funnelContext.insightColumnHintNoTrack")
        : t("funnelContext.insight.empty");

  const panelBody =
    total === 0 ? (
      <div className="ch-funnel-empty-pad">
        <p className="ch-help-legend__text" style={{ margin: 0 }}>
          {t("funnelContext.insight.empty")}
        </p>
      </div>
    ) : (
      <>
        <p
          style={{
            padding: "14px 18px 0",
            margin: 0,
            fontSize: 14,
            fontWeight: 700,
            color: "#374151",
            lineHeight: 1.45,
          }}
        >
          {t("funnelContext.whereQuestion", { days })}
        </p>
        <p
          style={{
            padding: "6px 18px 0",
            margin: 0,
            fontSize: 13,
            color: "#6b7280",
            lineHeight: 1.55,
            maxWidth: 720,
          }}
        >
          {t("funnelContext.panelIntroScope")}
        </p>
        <div className="ch-funnel-top-cols">
          <div className="ch-funnel-col ch-funnel-col--coverage">
            <p className="ch-funnel-col__title">{t("funnelContext.blockExitDetectedTitle")}</p>
            <div className="ch-funnel-coverage-split">
              <div
                className="ch-funnel-stage-split__pie ch-funnel-coverage-split__pie"
                style={{
                  background: funnelCoveragePieBackground(
                    total > 0 ? pctMain ?? 0 : 0,
                    total > 0 ? pctUntracked : 100
                  ),
                }}
                role="img"
                aria-label={t("funnelContext.coverageBarAria", {
                  tracked: wEp,
                  untracked: Math.max(0, total - wEp),
                  total,
                })}
              />
              <div className="ch-funnel-coverage-split__body">
                <p className="ch-funnel-metrics-lead">{t("funnelContext.blockExitDetectedLead", { tracked: wEp, total })}</p>
                <div className="ch-funnel-stack-meta">
                  <span className="ch-funnel-stack-meta__item ch-funnel-stack-meta__item--tracked">
                    {t("funnelContext.coverageMetaTracked", {
                      count: wEp,
                      pct: pctMain ?? 0,
                    })}
                  </span>
                  <span className="ch-funnel-stack-meta__item ch-funnel-stack-meta__item--untracked">
                    {t("funnelContext.coverageMetaUntracked", {
                      count: Math.max(0, total - wEp),
                      pct: pctUntracked,
                    })}
                  </span>
                </div>
                <p className="ch-funnel-coverage-text">
                  <strong>{t("funnelContext.coverageLabelShort")}</strong> {pctMain ?? 0}% — {t("funnelContext.coverageGrowthExplain")}
                </p>
                {wEp === 0 && total > 0 && (
                  <p className="ch-funnel-no-tracked-inline">{t("funnelContext.insight.noTracked")}</p>
                )}
              </div>
            </div>
          </div>

          <FunnelStagePctRows wEp={wEp} denom={denom} sc={sc} sch={sch} pc={pc} pch={pch} />

          <div className="ch-funnel-col ch-funnel-col--insight">
            <p className="ch-funnel-col__title">{t("funnelContext.insightColumnTitle")}</p>
            <p className="ch-funnel-col__desc ch-funnel-col__desc--insight">{insightBody}</p>
          </div>
        </div>
      </>
    );

  return (
    <section className="ch-help-legend ch-help-legend--funnel" style={{ marginBottom: 16 }} aria-label={t("funnelContext.ariaLabel")}>
      <div className="ch-help-legend__accent" aria-hidden="true" />
      {!isDesktop && (
        <button
          type="button"
          className="ch-help-legend__summary"
          aria-expanded={mobileOpen}
          aria-controls={panelId}
          onClick={() => setMobileOpen((o) => !o)}
        >
          <span className="ch-help-legend__sum-icon">
            <LegendInfoIcon size={18} />
          </span>
          <span>
            {t("funnelContext.summaryTitle")}
            <span className="ch-help-legend__sum-hint">{t("funnelContext.summaryHint", { days })}</span>
          </span>
          <span className="ch-help-legend__chev" aria-hidden="true" />
        </button>
      )}
      {showPanel && (
        <div id={panelId} className="ch-help-legend__panel">
          {panelBody}
        </div>
      )}
    </section>
  );
}

/** Banner obiettivo sequenza R — barra copertura + copy (recovery_pipeline_scope API). */
function RecoveryPipelineScopeBanner({ scope, money, t }) {
  if (!scope || typeof scope !== "object") return null;
  const days = Number(scope.window_days) || 30;
  const recv = Number(scope.recoverable_value) || 0;
  const n = Number(scope.recoverable_cart_count) || 0;
  let total = Number(scope.total_abandoned_value);
  if (!Number.isFinite(total)) {
    const fallbackOut = Number(scope.out_of_scope_value);
    total = Number.isFinite(fallbackOut) ? recv + Math.max(0, fallbackOut) : recv;
  }
  let out = Number(scope.out_of_scope_value);
  if (!Number.isFinite(out)) out = Math.max(0, total - recv);
  const totalBar = Math.max(total, recv + out, 0);
  const pctRecoverable = totalBar > 0 ? Math.min(100, (recv / totalBar) * 100) : 0;

  if (totalBar <= 0 && recv <= 0 && n <= 0) {
    return (
      <section className="ch-card" style={{ marginBottom: 14, padding: "14px 18px" }} aria-label={t("recoveryScope.ariaLabel")}>
        <p style={{ margin: 0, fontSize: 14, color: "#64748b", lineHeight: 1.55 }}>{t("recoveryScope.empty", { days })}</p>
      </section>
    );
  }

  const accent = "rgb(10, 133, 207)";
  const accentHi = "rgb(41, 175, 255)";
  const trackBg = "rgb(229, 231, 235)";
  const swatchOut = "rgb(211, 211, 211)";

  const bar = (
    <div
      style={{
        width: "100%",
        minWidth: 400,
        height: 15,
        display: "flex",
        borderRadius: 200,
        overflow: "hidden",
        background: trackBg,
      }}
      role="img"
      aria-label={t("recoveryScope.barAria", { pct: pctRecoverable.toFixed(1) })}
    >
      {pctRecoverable > 0 ? (
        <div
          style={{
            width: `${pctRecoverable}%`,
            minWidth: recv > 0 ? 2 : 0,
            height: "100%",
            background: `linear-gradient(90deg, ${accent}, ${accentHi})`,
            flexShrink: 0,
          }}
          aria-hidden
        />
      ) : null}
      <div style={{ width: `${100 - pctRecoverable}%`, minWidth: 0, height: "100%", background: trackBg, flexShrink: 0 }} aria-hidden />
    </div>
  );

  const legendRow = (bg, labelKey, amount) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, fontSize: 13, color: "#374151", lineHeight: 1.4 }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: bg, flexShrink: 0 }} aria-hidden />
      <span>
        <span style={{ fontWeight: 600 }}>{t(labelKey)}</span>
        <span style={{ color: "#6b7280" }}> · </span>
        <span>{money(amount)}</span>
      </span>
    </div>
  );

  const left = (
    <div
      style={{
        flex: "1 1 400px",
        minWidth: 0,
        maxWidth: 500,
        border: "1px solid #dedede",
        padding: 20,
        borderRadius: 14,
        background: "linear-gradient(115deg, rgba(246, 246, 246, 0.55) 0%, var(--surface-bright) 100%)",
        boxSizing: "border-box",
      }}
    >
      {bar}
      {legendRow(`linear-gradient(90deg, ${accent}, ${accentHi})`, "recoveryScope.legendRecoverable", recv)}
      {legendRow(swatchOut, "recoveryScope.legendOutOfScope", out)}
    </div>
  );

  const right = (
    <div style={{ flex: "1 1 280px", minWidth: 0 }}>
      <p style={{ margin: 0, fontSize: 16, fontWeight: 700, lineHeight: 1.45, color: "#374151" }}>
        <span style={{ color: accent }}>{money(recv)}</span>
        {t("recoveryScope.line1Remainder", { days })}
      </p>
      {n > 0 ? (
        <p style={{ margin: "10px 0 0", fontSize: 13, color: "#4b5563", lineHeight: 1.55 }}>{t("recoveryScope.line2", { count: n })}</p>
      ) : null}
    </div>
  );

  return (
    <section className="ch-card" style={{ marginBottom: 14, padding: "14px 18px" }} aria-label={t("recoveryScope.ariaLabel")}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 24,
          alignItems: "flex-start",
        }}
      >
        {left}
        {right}
      </div>
    </section>
  );
}

/** Legenda stati: fascia orizzontale su desktop, collassabile su mobile. */
function CartsStatusLegend() {
  const { t } = useTranslation("carts");
  const isDesktop = useMediaMinWidth(900);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!isDesktop) setMobileOpen(false);
  }, [isDesktop]);

  const panelId = "carts-status-legend-panel";
  const showPanel = isDesktop || mobileOpen;

  return (
    <section className="ch-help-legend" aria-label={t("legend.ariaLabel")}>
      <div className="ch-help-legend__accent" aria-hidden="true" />
      {!isDesktop && (
        <button
          type="button"
          className="ch-help-legend__summary"
          aria-expanded={mobileOpen}
          aria-controls={panelId}
          onClick={() => setMobileOpen((o) => !o)}
        >
          <span className="ch-help-legend__sum-icon">
            <LegendInfoIcon size={18} />
          </span>
          <span>
            {t("legend.summaryTitle")}
            <span className="ch-help-legend__sum-hint">{t("legend.summaryHint")}</span>
          </span>
          <span className="ch-help-legend__chev" aria-hidden="true" />
        </button>
      )}
      {showPanel && (
        <div id={panelId} className="ch-help-legend__panel">
          <header className="ch-help-legend__head ch-help-legend__head--mobile-only">
            <div className="ch-help-legend__head-icon" aria-hidden="true">
              <LegendInfoIcon />
            </div>
            <div>
              <h2 className="ch-help-legend__title">{t("legend.title")}</h2>
              <p className="ch-help-legend__sub">{t("legend.subMobile")}</p>
            </div>
          </header>

          <div className="ch-help-legend__desktop">
            <div className="ch-help-legend__desktop-intro">
              <div className="ch-help-legend__head-icon" aria-hidden="true">
                <LegendInfoIcon size={20} />
              </div>
              <div>
                <h2 className="ch-help-legend__title">{t("legend.title")}</h2>
                <p className="ch-help-legend__sub">{t("legend.subDesktop")}</p>
              </div>
            </div>
            <div className="ch-help-legend__grid ch-help-legend__grid--8">
              <div className="ch-help-legend__cell">
                <div className="ch-help-legend__cell-rail ch-help-legend__cell-rail--neutral" aria-hidden="true" />
                <span className="ch-status neutral">
                  <span className="status-dot" />
                  {t("badges.noEmail")}
                </span>
                <p className="ch-help-legend__cell-text">{t("statusHelpShort.provisional")}</p>
              </div>
              <div className="ch-help-legend__cell">
                <div className="ch-help-legend__cell-rail ch-help-legend__cell-rail--warning" aria-hidden="true" />
                <span className="ch-status warning">
                  <span className="status-dot" />
                  {t("badges.pending")}
                </span>
                <p className="ch-help-legend__cell-text">{t("statusHelpShort.pending")}</p>
              </div>
              <div className="ch-help-legend__cell">
                <div className="ch-help-legend__cell-rail ch-help-legend__cell-rail--success" aria-hidden="true" />
                <span className="ch-status success">
                  <span className="status-dot" />
                  {t("badges.recovered")}
                </span>
                <p className="ch-help-legend__cell-text">{t("statusHelpShort.recovered")}</p>
              </div>
              <div className="ch-help-legend__cell">
                <div className="ch-help-legend__cell-rail ch-help-legend__cell-rail--pa" aria-hidden="true" />
                <span className="ch-status assisted-pa">
                  <span className="status-dot" />
                  {t("badges.recoveredPa")}
                </span>
                <p className="ch-help-legend__cell-text">{t("statusHelpShort.assistedPa")}</p>
              </div>
              <div className="ch-help-legend__cell">
                <div className="ch-help-legend__cell-rail ch-help-legend__cell-rail--organic" aria-hidden="true" />
                <span className="ch-status organic">
                  <span className="status-dot" />
                  {t("badges.organic")}
                </span>
                <p className="ch-help-legend__cell-text">{t("statusHelpShort.organic")}</p>
              </div>
              <div className="ch-help-legend__cell">
                <div className="ch-help-legend__cell-rail ch-help-legend__cell-rail--completed" aria-hidden="true" />
                <span className="ch-status neutral">
                  <span className="status-dot" />
                  {t("badges.completed")}
                </span>
                <p className="ch-help-legend__cell-text">{t("statusHelpShort.completed")}</p>
              </div>
              <div className="ch-help-legend__cell">
                <div className="ch-help-legend__cell-rail ch-help-legend__cell-rail--success" aria-hidden="true" />
                <span className="ch-status success">
                  <span className="status-dot" />
                  {t("badges.popupOnsite")}
                </span>
                <p className="ch-help-legend__cell-text">{t("statusHelpShort.popupOnsite")}</p>
              </div>
              <div className="ch-help-legend__cell">
                <div className="ch-help-legend__cell-rail ch-help-legend__cell-rail--savecart" aria-hidden="true" />
                <span className="ch-status neutral" style={{ fontSize: 11 }} title={t("saveCart.title")}>
                  <span className="status-dot" />
                  {t("saveCart.badge")}
                </span>
                <p className="ch-help-legend__cell-text">{t("statusHelpShort.saveCart")}</p>
              </div>
            </div>
          </div>

          <div className="ch-help-legend__list">
            <div className="ch-help-legend__row">
              <div className="ch-help-legend__rail ch-help-legend__rail--neutral" aria-hidden="true" />
              <div className="ch-help-legend__body">
                <span className="ch-status neutral">
                  <span className="status-dot" />
                  {t("badges.noEmail")}
                </span>
                <p className="ch-help-legend__text">{t("statusHelp.provisional")}</p>
              </div>
            </div>
            <div className="ch-help-legend__row">
              <div className="ch-help-legend__rail ch-help-legend__rail--warning" aria-hidden="true" />
              <div className="ch-help-legend__body">
                <span className="ch-status warning">
                  <span className="status-dot" />
                  {t("badges.pending")}
                </span>
                <p className="ch-help-legend__text">{t("statusHelp.pending")}</p>
              </div>
            </div>
            <div className="ch-help-legend__row">
              <div className="ch-help-legend__rail ch-help-legend__rail--success" aria-hidden="true" />
              <div className="ch-help-legend__body">
                <span className="ch-status success">
                  <span className="status-dot" />
                  {t("badges.recovered")}
                </span>
                <p className="ch-help-legend__text">{t("statusHelp.recovered")}</p>
              </div>
            </div>
            <div className="ch-help-legend__row">
              <div className="ch-help-legend__rail ch-help-legend__rail--pa" aria-hidden="true" />
              <div className="ch-help-legend__body">
                <span className="ch-status assisted-pa">
                  <span className="status-dot" />
                  {t("badges.recoveredPa")}
                </span>
                <p className="ch-help-legend__text">{t("statusHelp.assistedPa")}</p>
              </div>
            </div>
            <div className="ch-help-legend__row">
              <div className="ch-help-legend__rail ch-help-legend__rail--organic" aria-hidden="true" />
              <div className="ch-help-legend__body">
                <span className="ch-status organic">
                  <span className="status-dot" />
                  {t("badges.organic")}
                </span>
                <p className="ch-help-legend__text">{t("statusHelp.organic")}</p>
              </div>
            </div>
            <div className="ch-help-legend__row">
              <div className="ch-help-legend__rail ch-help-legend__rail--completed" aria-hidden="true" />
              <div className="ch-help-legend__body">
                <span className="ch-status neutral">
                  <span className="status-dot" />
                  {t("badges.completed")}
                </span>
                <p className="ch-help-legend__text">{t("statusHelp.completed")}</p>
              </div>
            </div>
            <div className="ch-help-legend__row">
              <div className="ch-help-legend__rail ch-help-legend__rail--success" aria-hidden="true" />
              <div className="ch-help-legend__body">
                <span className="ch-status success">
                  <span className="status-dot" />
                  {t("badges.popupOnsite")}
                </span>
                <p className="ch-help-legend__text">{t("statusHelp.popupOnsite")}</p>
              </div>
            </div>
            <div className="ch-help-legend__row">
              <div className="ch-help-legend__rail ch-help-legend__rail--savecart" aria-hidden="true" />
              <div className="ch-help-legend__body">
                <span className="ch-status neutral" style={{ fontSize: 11 }} title={t("saveCart.title")}>
                  <span className="status-dot" />
                  {t("saveCart.badge")}
                </span>
                <p className="ch-help-legend__text">{t("statusHelp.saveCart")}</p>
              </div>
            </div>
          </div>

          <p className="ch-help-legend__tip">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M4 8V6a2 2 0 012-2h2M20 8V6a2 2 0 00-2-2h-2M4 16v2a2 2 0 002 2h2M20 16v2a2 2 0 01-2 2h-2" strokeLinecap="round" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <span>{t("legend.tip")}</span>
          </p>
        </div>
      )}
    </section>
  );
}

/** Email con @ = eleggibile per coda R1–R3 (allineato al filtro cron). */
function cartHasEmailForRecovery(cart) {
  const e = String(cart.customer_email || "").trim();
  return e.length > 0 && e.includes("@");
}

/** Ultimo `email_capture_source` nel payload webhook (`cart_data`), plugin WP — es. `save_cart`. */
function cartEmailCaptureSource(cart) {
  const d = cart?.cart_data;
  if (!d || typeof d !== "object" || Array.isArray(d)) return "";
  const s = d.email_capture_source;
  return typeof s === "string" ? s.trim() : "";
}

function cartUsedSaveCartWidget(cart) {
  return cartEmailCaptureSource(cart) === "save_cart";
}

/** True quando bonifica dedup ha marcato la riga come clone non leader (`cart_data._carthub_dedup_wc_order_loser`). */
function cartIsDedupWcOrderLoser(cart) {
  const d = cart?.cart_data;
  if (!d) return false;
  if (typeof d === "string") {
    try {
      const p = JSON.parse(d);
      return (
        p &&
        typeof p === "object" &&
        !Array.isArray(p) &&
        Object.prototype.hasOwnProperty.call(p, "_carthub_dedup_wc_order_loser")
      );
    } catch {
      return false;
    }
  }
  if (typeof d === "object" && !Array.isArray(d)) {
    return Object.prototype.hasOwnProperty.call(d, "_carthub_dedup_wc_order_loser");
  }
  return false;
}

/** `cart_data` legacy: array righe vs oggetto `{ items, wc_cart_has_coupon, … }` (plugin). */
function parseCartPayload(cartData) {
  if (!cartData) return { items: [], wc_cart_has_coupon: false };
  if (Array.isArray(cartData)) return { items: cartData, wc_cart_has_coupon: false };
  const items = Array.isArray(cartData.items) ? cartData.items : [];
  return { items, wc_cart_has_coupon: Boolean(cartData.wc_cart_has_coupon) };
}

/** Totale riga Woo al tracciamento: `line_total` → `line_subtotal` → `price × qty`. */
function lineTotalForDashboard(item) {
  const qtyRaw = Number(item.quantity);
  const qty = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;
  if (item.line_total != null && item.line_total !== "") {
    const n = Number(item.line_total);
    if (Number.isFinite(n)) return n;
  }
  if (item.line_subtotal != null && item.line_subtotal !== "") {
    const n = Number(item.line_subtotal);
    if (Number.isFinite(n)) return n;
  }
  const p = Number(item.price);
  return Number.isFinite(p) ? p * qty : NaN;
}

/** Ordine R1→R3 per colonna touchpoint (solo tipi reminder_*). */
const REMINDER_ORDER = { reminder_1: 1, reminder_2: 2, reminder_3: 3 };

/** Touchpoint: R1→R3 (+ PA); se nessuna mail e offerta popup confermata sul cloud, mostra “Popup”. */
function recoveryTouchpointSummary(cart, t) {
  const emails = cart.emails_sent || [];
  const types = [...new Set(emails.map((e) => e.type).filter(Boolean))]
    .filter((ty) => REMINDER_ORDER[ty])
    .sort((a, b) => REMINDER_ORDER[a] - REMINDER_ORDER[b]);
  if (types.length === 0) {
    if (cart.recovery_type === "assisted_pa") return t("touchpoint.paOnly");
    if (cart.attribution_channel === "onsite_popup") return t("touchpoint.popupOnsite");
    return "—";
  }
  const parts = types.map((ty) => t(`touchpoint.${ty}`));
  let s = parts.join(t("touchpoint.joiner"));
  if (cart.recovery_type === "assisted_pa") {
    s = t("touchpoint.paPrefix", { sequence: s });
  }
  return s;
}

/** Etichetta `attribution_channel` nel dettaglio (`attribution.*`); valori sconosciuti → non mostrare riga. */
function attributionChannelLabel(channel, t) {
  const ch = String(channel || "").trim();
  if (!ch) return null;
  const key = `attribution.${ch}`;
  const lbl = t(key);
  return lbl === key ? null : lbl;
}

function RecoveryTouchpointCell({ cart }) {
  const { t } = useTranslation("carts");
  const summary = recoveryTouchpointSummary(cart, t);
  return (
    <div className="td-sm" style={{ maxWidth: 168, lineHeight: 1.35 }} title={summary}>
      {summary}
    </div>
  );
}

// Badge di stato — recovery_type + attribuzione popup (attribution_channel da API / DB).
function CartStatusBadge({ cart }) {
  const { t } = useTranslation("carts");
  if (!cart.recovered) {
    if (cartIsDedupWcOrderLoser(cart)) {
      return (
        <span className="ch-status neutral" title={t("statusHelp.dedupWcOrderLoser")}>
          <span className="status-dot" />
          {t("badges.dedupWcOrderLoser")}
        </span>
      );
    }
    if (!cartHasEmailForRecovery(cart)) {
      return (
        <span className="ch-status neutral" title={t("statusHelp.provisional")}>
          <span className="status-dot" />
          {t("badges.noEmail")}
        </span>
      );
    }
    return (
      <span className="ch-status warning" title={t("statusHelp.pending")}>
        <span className="status-dot" />
        {t("badges.pending")}
      </span>
    );
  }
  if (cart.recovery_type === "completed" && cart.attribution_channel === "onsite_popup") {
    return (
      <span className="ch-status success" title={t("statusHelp.popupOnsite")}>
        <span className="status-dot" />
        {t("badges.popupOnsite")}
      </span>
    );
  }
  if (cart.recovery_type === "completed") {
    return (
      <span className="ch-status neutral" title={t("statusHelp.completed")}>
        <span className="status-dot" />
        {t("badges.completed")}
      </span>
    );
  }
  if (cart.recovery_type === "organic") {
    return (
      <span className="ch-status organic" title={t("statusHelp.organic")}>
        <span className="status-dot" />
        {t("badges.organic")}
      </span>
    );
  }
  if (cart.recovery_type === "assisted_pa") {
    return (
      <span className="ch-status assisted-pa" title={t("statusHelp.assistedPa")}>
        <span className="status-dot" />
        {t("badges.recoveredPa")}
      </span>
    );
  }
  return (
    <span className="ch-status success" title={t("statusHelp.recovered")}>
      <span className="status-dot" />
      {t("badges.recovered")}
    </span>
  );
}

function statusHelpForCart(cart, t) {
  if (!cart.recovered) {
    if (cartIsDedupWcOrderLoser(cart)) return t("statusHelp.dedupWcOrderLoser");
    return cartHasEmailForRecovery(cart) ? t("statusHelp.pending") : t("statusHelp.provisional");
  }
  if (cart.recovery_type === "completed" && cart.attribution_channel === "onsite_popup") return t("statusHelp.popupOnsite");
  if (cart.recovery_type === "completed") return t("statusHelp.completed");
  if (cart.recovery_type === "organic") return t("statusHelp.organic");
  if (cart.recovery_type === "assisted_pa") return t("statusHelp.assistedPa");
  return t("statusHelp.recovered");
}

/** Spiegazione R2 senza codice (API: r2_no_coupon_insight). */
function R2NoCouponInsightPanel({ insight, t, money }) {
  if (!insight?.code) return null;
  const { code, meta = {} } = insight;
  const thRaw = meta.line_promo_threshold_pct;
  const th =
    Number.isFinite(Number(thRaw)) && Number(thRaw) > 0 ? Number(thRaw) : 15;

  let body = null;
  if (code === "zero_budget") {
    const capPct = meta.cap_percent != null ? String(meta.cap_percent) : "—";
    const cartTotal = money(meta.cart_total_t);
    const tetto = money(meta.tetto_eur);
    const anticipi = money(meta.anticipi_eur);
    const ammissibile = money(meta.ammissibile_eur);
    body = (
      <>
        <p style={{ margin: "0 0 10px", fontSize: 14, lineHeight: 1.65, color: "#374151" }}>{t("detail.r2NoCouponBudgetLead")}</p>
        <p style={{ margin: "0 0 10px", fontSize: 14, lineHeight: 1.65, color: "#374151" }}>
          {t("detail.r2NoCouponBudgetBody", { capPct, cartTotal, tetto, anticipi, ammissibile })}
        </p>
        {meta.wc_cart_had_coupon ? (
          <p style={{ margin: "0 0 12px", fontSize: 13, lineHeight: 1.6, color: "#4b5563" }}>{t("detail.r2NoCouponBudgetWcCouponHint")}</p>
        ) : null}
        {meta.exemplar ? (
          <div
            style={{
              margin: "12px 0 0",
              padding: "10px 12px",
              background: "#f9fafb",
              borderRadius: 6,
              border: "1px solid #e5e7eb",
            }}
          >
            <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 600, letterSpacing: "0.02em", color: "#6b7280", textTransform: "uppercase" }}>
              {t("detail.r2NoCouponBudgetTriggerHeading")}
            </p>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: "#4b5563" }}>
              {t("detail.r2NoCouponBudgetExample", {
                product: meta.exemplar.name || "—",
                regular: money(meta.exemplar.regular_price),
                paid: money(meta.exemplar.line_subtotal),
                pct: String(meta.exemplar.catalog_discount_pct),
              })}
            </p>
          </div>
        ) : null}
      </>
    );
  } else if (code === "policy_blocked") {
    body = meta.wc_cart_had_coupon ? (
      <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: "#374151" }}>{t("detail.r2NoCouponPolicyCart")}</p>
    ) : (
      <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: "#374151" }}>{t("detail.r2NoCouponPolicyLine", { thresholdPct: th })}</p>
    );
  } else if (code === "insufficient_data") {
    body = <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: "#374151" }}>{t("detail.r2NoCouponInsufficientData")}</p>;
  } else if (code === "snapshot_lines_empty") {
    body = <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: "#374151" }}>{t("detail.r2NoCouponSnapshotEmpty")}</p>;
  } else if (code === "zero_cap") {
    body = (
      <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: "#374151" }}>
        {t("detail.r2NoCouponZeroCap", {
          capPct: meta.effective_cap_percent != null ? String(meta.effective_cap_percent) : "0",
        })}
      </p>
    );
  } else if (code === "strategy_no_discount") {
    body = <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: "#374151" }}>{t("detail.r2NoCouponStrategy")}</p>;
  }

  if (!body) return null;

  return (
    <div className="ch-card" style={{ marginTop: 16 }}>
      <h2 style={{ marginTop: 0 }}>{t("detail.r2NoCouponTitle")}</h2>
      <hr className="ch-divider" style={{ margin: "12px 0 16px" }} />
      {body}
    </div>
  );
}

function CartDetail({ cart: cartFromList, onBack, currencyCode = "EUR", shopDomain = "" }) {
  const { t, i18n } = useTranslation("carts");
  const [cart, setCart] = useState(cartFromList);
  useEffect(() => {
    setCart(cartFromList);
    let cancelled = false;
    const id = cartFromList?.id;
    if (!id) return undefined;
    api(`/carts/${encodeURIComponent(id)}`).then((d) => {
      if (cancelled || !d || typeof d !== "object" || d.error || !d.id) return;
      setCart(d);
    });
    return () => {
      cancelled = true;
    };
    // Solo id: evitiamo refetch quando la lista passa un nuovo oggetto con stesso carrello.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartFromList.id]);
  const money = (n) => formatMoney(i18n.language, currencyCode, n);
  const { items, wc_cart_has_coupon } = parseCartPayload(cart.cart_data);
  const emails = cart.emails_sent || [];
  const emailLabels = {
    reminder_1: t("emailTypes.reminder_1"),
    reminder_2: t("emailTypes.reminder_2"),
    reminder_3: t("emailTypes.reminder_3"),
  };
  const none = "—";
  const attributionLabel = attributionChannelLabel(cart.attribution_channel, t);
  const customerTableRows = (() => {
    const rows = [
      [
        t("detail.rowCartId"),
        <span style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <code style={{ fontSize: 13, wordBreak: "break-all", color: "#374151" }}>{cart.id}</code>
          <CopyBtn text={cart.id} />
        </span>,
      ],
      [t("detail.rowName"), cart.customer_name || none],
      [t("detail.rowEmail"), cart.customer_email],
      [t("detail.rowLanguage"), <span className="ch-status neutral">{(cart.language || "it").toUpperCase()}</span>],
      [t("detail.rowAbandoned"), ago(cart.abandoned_at)],
      [t("detail.rowState"), <CartStatusBadge cart={cart} />],
      [t("detail.rowTouchpoint"), recoveryTouchpointSummary(cart, t)],
    ];
    if (attributionLabel) rows.push([t("detail.rowAttribution"), attributionLabel]);
    rows.push([t("detail.rowSaveCart"), cartUsedSaveCartWidget(cart) ? t("saveCart.detailYes") : t("saveCart.detailNo")]);
    if (cart.exit_page && String(cart.exit_page).trim()) {
      rows.push([t("detail.rowExitPage"), truncateDisplay(cart.exit_page, 140)]);
    }
    if (cart.device_type && String(cart.device_type).trim()) {
      rows.push([t("detail.rowDeviceType"), cart.device_type]);
    }
    if (cart.traffic_source && String(cart.traffic_source).trim()) {
      rows.push([t("detail.rowTrafficSource"), truncateDisplay(cart.traffic_source, 200)]);
    }
    rows.push([
      t("detail.rowCartValue"),
      <>
        <div>{money(cart.cart_value)}</div>
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4, lineHeight: 1.45, maxWidth: 380 }}>
          {t("detail.pricingDisclaimerShort")}
        </div>
      </>,
    ]);
    const showRecoveredMetric =
      cart.recovered &&
      cart.recovery_type !== "completed" &&
      cart.recovered_value != null &&
      String(cart.recovered_value).trim() !== "";
    if (showRecoveredMetric) {
      rows.push([
        t("detail.rowRecoveredValue"),
        <>
          <div>
            <span style={{ fontWeight: 600, color: "#0A85CF" }}>{money(cart.recovered_value)}</span>
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6, lineHeight: 1.55, maxWidth: 440 }}>
            {t("detail.recoveredValueLegend")}
          </div>
        </>,
      ]);
    }
    if (cart.recovered && cart.wc_order_id && String(cart.wc_order_id).trim() !== "") {
      const wooHref = wcOrderEditUrl(shopDomain, cart.wc_order_id);
      const oid = String(cart.wc_order_id).trim();
      rows.push([
        t("detail.rowWooOrder"),
        wooHref ? (
          <a
            href={wooHref}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontWeight: 500, color: "#0A85CF", textDecoration: "underline" }}
          >
            {t("detail.openWooOrder", { id: oid })}
          </a>
        ) : (
          <code style={{ fontSize: 13, wordBreak: "break-all", color: "#374151" }}>{oid}</code>
        ),
      ]);
    }
    return rows;
  })();

  return (
    <div>
      <button className="ch-btn ch-btn-ghost ch-btn-sm" onClick={onBack} style={{ marginBottom: 16 }}>
        {Ic.back} {t("detail.back")}
      </button>
      <div className="ch-info" style={{ marginBottom: 16 }}>
        <strong style={{ color: "#1a1a1a" }}>{t("detail.stateTitle")}</strong>
        <p style={{ margin: "8px 0 0", fontSize: 14, lineHeight: 1.65, color: "#4b5563" }}>{statusHelpForCart(cart, t)}</p>
        {cart.recovery_hint && (
          <p style={{ margin: "12px 0 0", fontSize: 14, lineHeight: 1.65, color: "#374151", borderLeft: "3px solid #b2d8d1", paddingLeft: 12 }}>
            <strong style={{ display: "block", marginBottom: 4, color: "#1a1a1a" }}>{t("detail.recoveryQueueLabel")}</strong>
            {recoveryHintText(cart.recovery_hint, t, i18n.language)}
          </p>
        )}
      </div>
      <div className="grid-2">
        <div>
          <div className="ch-card">
            <h2>{t("detail.customerTitle")}</h2>
            <hr className="ch-divider" style={{ margin: "12px 0 16px" }} />
            <table className="ch-status-table">
              <tbody>
                {customerTableRows.map(([k, v]) => (
                  <tr key={String(k)}>
                    <th>{k}</th>
                    <td>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="ch-card">
            <h2>{t("detail.emailsTitle")}</h2>
            <hr className="ch-divider" style={{ margin: "12px 0 16px" }} />
            {emails.length === 0 ? (
              <div className="ch-info">{t("detail.noEmailsYet")}</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {emails.map((e, i) => (
                  <div key={i} style={{ padding: 12, background: "#f9fafb", borderRadius: 6, border: "1px solid #e5e5e5" }}>
                    <div style={{ fontWeight: 500, fontSize: 14, marginBottom: 4 }}>{emailLabels[e.type] || e.type}</div>
                    {e.subject && (
                      <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 6, fontStyle: "italic" }}>&quot;{e.subject}&quot;</div>
                    )}
                    {(e.offer_percent != null && e.offer_percent !== "") ||
                    (e.offer_fixed_eur != null && e.offer_fixed_eur !== "") ? (
                      <div style={{ fontSize: 13, color: "#374151", marginBottom: 6, lineHeight: 1.45 }}>
                        {e.offer_percent != null &&
                          e.offer_percent !== "" &&
                          Number.isFinite(Number(e.offer_percent)) &&
                          Number(e.offer_percent) > 0 && (
                            <span style={{ display: "block" }}>
                              {t("detail.emailOfferStrategicPct", {
                                pct: Number(e.offer_percent).toLocaleString(i18n.language, {
                                  maximumFractionDigits: 2,
                                }),
                              })}
                            </span>
                          )}
                        {e.offer_fixed_eur != null &&
                          e.offer_fixed_eur !== "" &&
                          Number.isFinite(Number(e.offer_fixed_eur)) &&
                          Number(e.offer_fixed_eur) > 0 && (
                            <span style={{ display: "block" }}>
                              {t("detail.emailOfferFixedEur", { amount: money(e.offer_fixed_eur) })}
                            </span>
                          )}
                      </div>
                    ) : null}
                    <div style={{ display: "flex", gap: 8, fontSize: 13 }}>
                      <span className="ch-status neutral">{ago(e.sent_at)}</span>
                      {e.opened && <span className="ch-status success">{t("detail.opened")}</span>}
                      {e.clicked && <span className="ch-status success">{t("detail.clicked")}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div>
          <div className="ch-card">
            <h2>{t("detail.productsTitle")}</h2>
            <hr className="ch-divider" style={{ margin: "12px 0 16px" }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {items.map((item, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 12,
                    padding: 12,
                    background: "#f9fafb",
                    borderRadius: 6,
                    border: "1px solid #e5e5e5",
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      background: "#f0faf8",
                      border: "1px solid #b2d8d1",
                      borderRadius: 6,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      color: "#0A85CF",
                    }}
                  >
                    <span style={{ display: "flex", width: 18, height: 18 }}>{Ic.package}</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: 15 }}>{item.name}</div>
                    <div className="td-sm">
                      {t("detail.qty")}: {item.quantity || 1} · {item.category || none}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", minWidth: 104 }}>
                    {(() => {
                      const lt = lineTotalForDashboard(item);
                      const qty = Number(item.quantity) || 1;
                      const pu = Number(item.price);
                      const unitOk = Number.isFinite(pu);
                      const main = Number.isFinite(lt) ? lt : unitOk ? pu * qty : NaN;
                      return (
                        <>
                          <div style={{ fontWeight: 600, color: "#0A85CF" }}>{Number.isFinite(main) ? money(main) : none}</div>
                          {unitOk ? (
                            <div className="td-sm" style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                              {t("detail.lineUnitHint", { price: money(pu) })}
                            </div>
                          ) : null}
                        </>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
            <hr className="ch-divider" />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                <span style={{ fontWeight: 500, display: "block" }}>{t("detail.cartTotal")}</span>
                <span style={{ fontSize: 12, color: "#6b7280", display: "block", marginTop: 4, lineHeight: 1.45 }}>
                  {t("detail.pricingDisclaimerDetail")}
                </span>
                {wc_cart_has_coupon ? (
                  <span className="ch-status neutral" style={{ marginTop: 8, fontSize: 12, display: "inline-flex" }}>
                    {t("detail.couponAppliedBadge")}
                  </span>
                ) : null}
              </div>
              <span style={{ fontSize: 18, fontWeight: 600, color: "#0A85CF", flexShrink: 0 }}>{money(cart.cart_value)}</span>
            </div>
          </div>
          <R2NoCouponInsightPanel insight={cart.r2_no_coupon_insight} t={t} money={money} />
        </div>
      </div>
    </div>
  );
}

export default function Carts({ shopId, shopDomain = "", currencyCode = "EUR" }) {
  const { t, i18n } = useTranslation("carts");
  const money = (n) => formatMoney(i18n.language, currencyCode, n);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState(null);
  const [carts, setCarts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [listRefreshing, setListRefreshing] = useState(false);
  const [funnelContext, setFunnelContext] = useState(null);
  const [recoveryPipelineScope, setRecoveryPipelineScope] = useState(null);
  const [hintModal, setHintModal] = useState(null);
  const closeHintModal = useCallback(() => setHintModal(null), []);
  const PER_PAGE = 20;

  useEffect(() => {
    const tmr = setTimeout(() => setDebouncedSearch(searchInput.trim()), 350);
    return () => clearTimeout(tmr);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, shopId, filter]);

  const fetchCarts = useCallback(
    (opts = { soft: false }) => {
      if (!shopId) return Promise.resolve();
      if (opts.soft) setListRefreshing(true);
      else {
        setLoading(true);
        setFunnelContext(null);
        setRecoveryPipelineScope(null);
      }
      const qs = new URLSearchParams({
        limit: String(PER_PAGE),
        offset: String(page * PER_PAGE),
        filter,
      });
      if (debouncedSearch) qs.set("search", debouncedSearch);
      return api(`/carts/shop/${shopId}?${qs.toString()}`)
        .then((d) => {
          const rows = Array.isArray(d) ? d : d.carts || [];
          setCarts(rows);
          setTotal(typeof d.total === "number" ? d.total : rows.length);
          if (!opts.soft) {
            setFunnelContext(d.funnel_context && typeof d.funnel_context === "object" ? d.funnel_context : null);
            setRecoveryPipelineScope(d.recovery_pipeline_scope && typeof d.recovery_pipeline_scope === "object" ? d.recovery_pipeline_scope : null);
          } else if (d.funnel_context && typeof d.funnel_context === "object") {
            setFunnelContext(d.funnel_context);
          }
          if (opts.soft && d.recovery_pipeline_scope && typeof d.recovery_pipeline_scope === "object") {
            setRecoveryPipelineScope(d.recovery_pipeline_scope);
          }
        })
        .catch(() => {})
        .finally(() => {
          if (opts.soft) setListRefreshing(false);
          else setLoading(false);
        });
    },
    [shopId, page, debouncedSearch, filter]
  );

  useEffect(() => {
    fetchCarts();
  }, [fetchCarts]);

  useEffect(() => {
    const tp = Math.ceil(total / PER_PAGE);
    if (tp === 0) {
      setPage(0);
      return;
    }
    setPage((p) => Math.min(p, tp - 1));
  }, [total]);

  const list = carts;

  const cartItems = (c) => {
    const { items } = parseCartPayload(c.cart_data);
    return items.map((i) => i.name).filter(Boolean).join(", ");
  };

  const totalPages = Math.ceil(total / PER_PAGE);

  if (sel) return <CartDetail cart={sel} onBack={() => setSel(null)} currencyCode={currencyCode} shopDomain={shopDomain} />;
  return (
    <div>
      {hintModal != null && <StatusHintModal title={hintModal.title} message={hintModal.message} onClose={closeHintModal} />}
      <FunnelContextCoverageCard fc={funnelContext} />
      <CartsStatusLegend />
      <RecoveryPipelineScopeBanner scope={recoveryPipelineScope} money={money} t={t} />
      <div className="carts-toolbar">
        <input className="ch-search" placeholder={t("toolbar.searchPlaceholder")} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
        <div className="carts-toolbar-filters">
          <button
            type="button"
            className={`ch-btn ch-btn-sm carts-toolbar-refresh${listRefreshing ? " is-spinning" : ""}`}
            onClick={() => fetchCarts({ soft: true })}
            disabled={loading || listRefreshing}
            aria-label={t("toolbar.refreshAria")}
            title={t("toolbar.refresh")}
          >
            {Ic.refresh}
            <span className="carts-toolbar-refresh-label">{t("toolbar.refresh")}</span>
          </button>
          {[
            ["all", t("toolbar.filterAll")],
            ["e", t("toolbar.filterWithEmail")],
            ["rq", t("toolbar.filterRSequence")],
            ["p", t("toolbar.filterPending")],
            ["r", t("toolbar.filterRecovered")],
            ["o", t("toolbar.filterOrganic")],
            ["sc", t("toolbar.filterSaveCart")],
          ].map(([k, v]) => (
            <button
              key={k}
              className={`ch-btn ch-btn-sm${filter === k ? " ch-btn-primary" : ""}`}
              onClick={() => {
                setFilter(k);
                setPage(0);
              }}
            >
              {v}
            </button>
          ))}
        </div>
      </div>
      <div className="ch-card ch-card-table-shell" style={{ marginBottom: 0 }}>
        <div className="table-wrap table-min-wide">
          {loading ? (
            <div className="empty">
              <div className="empty-icon-svg" aria-hidden>
                {Ic.loader}
              </div>
              <div className="empty-title">{t("list.loading")}</div>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>{t("table.client")}</th>
                  <th>{t("table.products")}</th>
                  <th>{t("table.value")}</th>
                  <th>{t("table.abandoned")}</th>
                  <th>{t("table.language")}</th>
                  <th>{t("table.email")}</th>
                  <th>{t("table.touchpoint")}</th>
                  <th>{t("table.state")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div style={{ fontWeight: 500, display: "flex", alignItems: "center", minHeight: 22 }}>
                        {c.customer_name ? c.customer_name : <CartNamePlaceholder />}
                      </div>
                      <div className="td-sm">{c.customer_email}</div>
                    </td>
                    <td>
                      <div style={{ fontSize: 14, color: "#6b7280", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {cartItems(c) || "—"}
                      </div>
                    </td>
                    <td style={{ fontWeight: 600, color: "#0A85CF" }} title={t("detail.pricingDisclaimerShort")}>
                      {c.recovered &&
                      c.recovery_type !== "completed" &&
                      c.recovered_value &&
                      parseFloat(c.recovered_value) < parseFloat(c.cart_value) ? (
                        <>
                          <span style={{ textDecoration: "line-through", color: "#9ca3af", fontWeight: 400, fontSize: 12 }}>{money(c.cart_value)}</span>{" "}
                          {money(c.recovered_value)}
                        </>
                      ) : (
                        money(c.cart_value)
                      )}
                    </td>
                    <td className="td-muted">{ago(c.abandoned_at)}</td>
                    <td>
                      <span className="ch-status neutral">{(c.language || "it").toUpperCase()}</span>
                    </td>
                    <td>
                      <span className="ch-status neutral">{t("list.emailsSentShort", { count: (c.emails_sent || []).length })}</span>
                    </td>
                    <td>
                      <RecoveryTouchpointCell cart={c} />
                    </td>
                    <td style={{ verticalAlign: "middle" }}>
                      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                        <CartStatusBadge cart={c} />
                        {!c.recovered && !cartHasEmailForRecovery(c) && (
                          <ProvisionalHintTrigger
                            onOpen={() =>
                              setHintModal({
                                title: t("provisionalModal.title"),
                                message: t("provisionalModal.message"),
                              })
                            }
                          />
                        )}
                        {c.recovery_hint && (
                          <RecoveryQueueHintTrigger
                            onOpen={() =>
                              setHintModal({
                                title: t("recoveryQueueTitle"),
                                message: recoveryHintText(c.recovery_hint, t, i18n.language),
                              })
                            }
                          />
                        )}
                        {cartUsedSaveCartWidget(c) && (
                          <span className="ch-status neutral" title={t("saveCart.title")} style={{ fontSize: 11, marginLeft: 2 }}>
                            {t("saveCart.badge")}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <button className="ch-btn ch-btn-ghost ch-btn-sm" onClick={() => setSel(c)}>
                        {t("table.detail")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!loading && list.length === 0 && (
            <div className="empty">
              <div className="empty-icon-svg" aria-hidden>
                {Ic.cart}
              </div>
              <div className="empty-title">{t("list.emptyTitle")}</div>
              <div>{t("list.emptyHint")}</div>
            </div>
          )}
          {!loading && totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 12 }}>
              <button className="pill" disabled={page === 0} style={{ cursor: page === 0 ? "default" : "pointer", opacity: page === 0 ? 0.5 : 1 }} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                {t("pagination.prev")}
              </button>
              <span style={{ fontSize: 13, color: "#6b7280", alignSelf: "center" }}>
                {page + 1} / {totalPages}
              </span>
              <button
                className="pill"
                disabled={page >= totalPages - 1}
                style={{ cursor: page >= totalPages - 1 ? "default" : "pointer", opacity: page >= totalPages - 1 ? 0.5 : 1 }}
                onClick={() => setPage((p) => p + 1)}
              >
                {t("pagination.next")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
